/* ============================================================
   SkillSync Pro API Client
   Production Backend
   ============================================================ */

export const API_BASE =
  'https://skillsyncpro.in/backend-php';

const TOKEN_KEY = 'ssp_admin_token';


/* ============================================================
   AUTHENTICATION HELPERS
   ============================================================ */

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function isLoggedIn() {
  return !!getToken();
}


/* ============================================================
   COMMON REQUEST HELPER
   ============================================================ */

async function request(path, options = {}) {
  const url = `${API_BASE}${path}`;

  let response;

  try {
    response = await fetch(url, {
      ...options,

      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    });
  } catch (error) {
    console.error('API connection error:', error);

    throw new Error(
      `Unable to connect to server.\n\nAPI URL:\n${url}`
    );
  }


  /* ----------------------------------------------------------
     Read response as text first.
     This helps when PHP returns HTML error output.
     ---------------------------------------------------------- */

  const rawText = await response.text();

  let data = null;

  if (rawText) {
    try {
      data = JSON.parse(rawText);
    } catch (error) {
      console.error(
        'Non-JSON response from server:',
        rawText
      );

      const cleaned = rawText
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .trim();

      if (cleaned) {
        throw new Error(
          `Server returned an invalid response.\n\n${cleaned.slice(
            0,
            500
          )}`
        );
      }

      throw new Error(
        `Server returned an empty/invalid response.\n\nURL:\n${url}`
      );
    }
  }


  /* ----------------------------------------------------------
     HTTP ERROR
     ---------------------------------------------------------- */

  if (!response.ok) {
    const message =
      data?.error ||
      data?.message ||
      `Request failed (${response.status})`;

    if (response.status === 401) {
      clearToken();
    }

    throw new Error(message);
  }


  if (data === null) {
    throw new Error(
      'Server returned an empty response.'
    );
  }

  return data;
}


/* ============================================================
   PUBLIC API — TRAININGS
   ============================================================ */

export function fetchTrainings() {
  return request('/trainings.php');
}

export function fetchTraining(id) {
  return request(
    `/trainings.php?id=${encodeURIComponent(id)}`
  );
}


/* ============================================================
   PUBLIC API — PORTFOLIO
   ============================================================ */

export function fetchPortfolio() {
  return request('/portfolio.php');
}

export function fetchPortfolioItem(id) {
  return request(
    `/portfolio.php?id=${encodeURIComponent(id)}`
  );
}


/* ============================================================
   PUBLIC API — CAREERS
   ============================================================ */

export function fetchCareers() {
  return request('/careers.php');
}

export function fetchCareer(id) {
  return request(
    `/careers.php?id=${encodeURIComponent(id)}`
  );
}


/* ============================================================
   ADMIN LOGIN
   ============================================================ */

export async function adminLogin(username, password) {
  const data = await request('/login.php', {
    method: 'POST',

    headers: {
      'Content-Type': 'application/json',
    },

    body: JSON.stringify({
      username,
      password,
    }),
  });

  if (!data || !data.token) {
    throw new Error(
      'Login succeeded but server did not return an authentication token.'
    );
  }

  setToken(data.token);

  return data;
}


/* ============================================================
   ADMIN CRUD — TRAININGS
   ============================================================ */

export function createTraining(formData) {
  return request('/admin/create.php', {
    method: 'POST',

    headers: {
      Authorization: `Bearer ${getToken()}`,
    },

    body: formData,
  });
}

export function updateTraining(id, formData) {
  formData.append('id', id);

  return request('/admin/update.php', {
    method: 'POST',

    headers: {
      Authorization: `Bearer ${getToken()}`,
    },

    body: formData,
  });
}

export function deleteTraining(id) {
  return request('/admin/delete.php', {
    method: 'POST',

    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },

    body: JSON.stringify({
      id,
    }),
  });
}


/* ============================================================
   ADMIN CRUD — PORTFOLIO
   ============================================================ */

export function createPortfolioItem(formData) {
  return request('/admin/portfolio_create.php', {
    method: 'POST',

    headers: {
      Authorization: `Bearer ${getToken()}`,
    },

    body: formData,
  });
}

export function updatePortfolioItem(id, formData) {
  formData.append('id', id);

  return request('/admin/portfolio_update.php', {
    method: 'POST',

    headers: {
      Authorization: `Bearer ${getToken()}`,
    },

    body: formData,
  });
}

export function deletePortfolioItem(id) {
  return request('/admin/portfolio_delete.php', {
    method: 'POST',

    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },

    body: JSON.stringify({
      id,
    }),
  });
}


/* ============================================================
   INTERNSHIP APPLICATION — PUBLIC
   ============================================================ */

export function submitInternshipApplication(payload) {
  return request('/internship_apply.php', {
    method: 'POST',

    headers: {
      'Content-Type': 'application/json',
    },

    body: JSON.stringify(payload),
  });
}


/* ============================================================
   ADMIN — INTERNSHIP APPLICATIONS
   ============================================================ */

export function fetchInternshipApplications() {
  return request(
    '/admin/internship_applications.php',
    {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    }
  );
}

export function updateInternshipApplicationStatus(
  id,
  status
) {
  return request(
    '/admin/internship_applications_update.php',
    {
      method: 'POST',

      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
      },

      body: JSON.stringify({
        id,
        status,
      }),
    }
  );
}

export function deleteInternshipApplication(id) {
  return request(
    '/admin/internship_applications_delete.php',
    {
      method: 'POST',

      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
      },

      body: JSON.stringify({
        id,
      }),
    }
  );
}


/* ============================================================
   ADMIN CRUD — CAREERS
   ============================================================ */

export function createCareer(payload) {
  return request('/admin/careers_create.php', {
    method: 'POST',

    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },

    body: JSON.stringify(payload),
  });
}

export function updateCareer(id, payload) {
  return request('/admin/careers_update.php', {
    method: 'POST',

    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },

    body: JSON.stringify({
      ...payload,
      id,
    }),
  });
}

export function deleteCareer(id) {
  return request('/admin/careers_delete.php', {
    method: 'POST',

    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },

    body: JSON.stringify({
      id,
    }),
  });
}


/* ============================================================
   IMAGE URL HELPER
   ============================================================ */

export function imageUrl(relativePath) {
  if (!relativePath) {
    return null;
  }

  /* Already a complete URL */
  if (/^https?:\/\//i.test(relativePath)) {
    return relativePath;
  }

  /* Relative path beginning with / */
  if (relativePath.startsWith('/')) {
    return `${API_BASE}${relativePath}`;
  }

  /* Relative path without / */
  return `${API_BASE}/${relativePath}`;
}